import React from 'react';
import { Dialog, DialogActionsBar } from '@progress/kendo-react-dialogs';
import { useLDClient } from '@launchdarkly/react-sdk';
import { logout } from '../../../utils/auth-manager';
import { useIdleTimer } from '../../../hooks/use-idle-timer';

const DEFAULT_IDLE_MINUTES = 10;
const DEFAULT_GRACE_MINUTES = 2;

const formatCountdown = (totalSeconds: number): string => {
  const safeSeconds = Math.max(0, totalSeconds);
  const minutes = Math.floor(safeSeconds / 60);
  const seconds = safeSeconds % 60;
  return `${String(minutes).padStart(2, '0')}:${String(seconds).padStart(
    2,
    '0',
  )}`;
};

const SessionTimeoutModal: () => React.ReactElement | null = () => {
  const ldClient = useLDClient();

  const idleMinutes = (ldClient?.variation(
    'session-inactivity-timeout-minutes',
    DEFAULT_IDLE_MINUTES,
  ) ?? DEFAULT_IDLE_MINUTES) as number;

  const graceMinutes = (ldClient?.variation(
    'session-grace-period-minutes',
    DEFAULT_GRACE_MINUTES,
  ) ?? DEFAULT_GRACE_MINUTES) as number;

  const handleTimeout = React.useCallback(() => {
    logout();
  }, []);

  const { showPrompt, countdown, resetTimer } = useIdleTimer({
    idleThresholdMs: idleMinutes * 60 * 1000,
    gracePeriodMs: graceMinutes * 60 * 1000,
    onTimeout: handleTimeout,
  });

  if (!showPrompt) {
    return null;
  }

  return (
    <Dialog
      id="session-timeout-modal"
      className="session-timeout-dialog tw-z-[10009]"
      title="Need a little more time?"
      closeIcon={false}
    >
      <p className="tw-text-sm tw-font-semibold tw-text-unm-primary01-100">
        Your session is about to expire due to inactivity.
      </p>
      <p
        aria-live="polite"
        className="tw-mt-3 tw-text-sm tw-font-semibold tw-text-unm-primary01-100"
      >
        You will be logged out in {formatCountdown(countdown)}.
      </p>
      <DialogActionsBar>
        <div className="tw-flex tw-justify-end tw-gap-3 tw-pt-2">
          <button
            id="session-timeout-modal-logout-button"
            type="button"
            onClick={() => logout()}
            className="tw-rounded tw-border tw-border-unm-primary02-400 tw-bg-white tw-px-6 tw-py-2 tw-text-sm tw-font-semibold tw-text-unm-primary02-400 hover:tw-bg-unm-primary02-100"
          >
            Log out
          </button>
          <button
            id="session-timeout-modal-continue-button"
            type="button"
            onClick={resetTimer}
            className="tw-rounded tw-border tw-border-unm-primary02-400 tw-bg-unm-primary02-400 tw-px-6 tw-py-2 tw-text-sm tw-font-semibold tw-text-white hover:tw-bg-unm-primary02-500"
          >
            Continue session
          </button>
        </div>
      </DialogActionsBar>
    </Dialog>
  );
};

export { SessionTimeoutModal };
